import { useLazyQuery } from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import Moment from 'moment';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { STATUS_TYPE } from 'constants/app';
import { CHECK_TWO_FACTOR_AUTH } from 'queries';
import { delayButtonState, sessionTokenState } from 'states/app';
import { notificationState } from 'states/notification';
import { errorWithBackend } from 'utils/Helpers';

const useVerifyToken = (accessReportId, refetch, idButton) => {
  const navigate = useNavigate();
  const resetDelayButton = useResetAtom(delayButtonState(idButton));
  const setNotification = useSetAtom(notificationState);
  const setSessionToken = useSetAtom(sessionTokenState);

  const [query, { called, loading, data, error }] = useLazyQuery(
    CHECK_TWO_FACTOR_AUTH,
    {
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    if (!called || loading) return;

    error &&
      errorWithBackend(
        error,
        navigate,
        accessReportId,
        setNotification,
        refetch
      );

    resetDelayButton();
    switch (data?.result?.statusType) {
      case STATUS_TYPE.CHANGED_TO_SMS:
        navigate(`/verify/${accessReportId}`);
        break;

      case STATUS_TYPE.SUCCESS:
        setSessionToken({
          value: data.result.sessionToken,
          datetime: Moment().format(),
        });
        navigate(`/documents/${accessReportId}`);
        break;

      default:
        refetch();
        navigate(`/remainingAttempts/${accessReportId}`);
        break;
    }
  }, [
    called,
    loading,
    data,
    error,
    resetDelayButton,
    navigate,
    accessReportId,
    setNotification,
    setSessionToken,
    refetch,
  ]);

  const onVerify = useCallback(
    (token) => {
      return query({
        variables: {
          accessReportId,
          token,
        },
      });
    },
    [accessReportId, query]
  );

  return { loading, onVerify };
};

export default useVerifyToken;
